import axios from 'axios';
import { z } from 'zod';

import Sentry from '@lib/analytics/sentry';
import client from '@lib/api/client';
import {
    ApiClientError,
    type MalformedResponse,
    MalformedResponseError,
    NetworkError,
    isMalformedResponse,
    isMalformedResponseError
} from '@lib/api/errors';
import { makeApiParser } from '@lib/api/validation';
import { Either } from '@lib/monad';

type KeyType = number | string | symbol;
export type QueryParams<K extends KeyType = string> = Record<K, string | number>;

export type ExtractParams<T extends string> = string extends T
    ? object
    : T extends `${string}:${infer Param}/${infer Rest}`
      ? QueryParams<Param | keyof ExtractParams<Rest>>
      : T extends `${string}:${infer Param}`
        ? QueryParams<Param>
        : object;

interface ApiEndpointDeclaration<Schema extends z.ZodType, Path extends string = string> {
    path: Path;
    schema: Schema;
    method: 'post' | 'put' | 'delete';
}

/**
 * Used to declare a (write) REST api endpoint call. Unfortunately the typeof declaration is required.
 *
 *     declare<EndpointParams, typeof $entitySchema>({
 *        path: 'v3/endpoint-name',
 *        schema: $entitySchema,
 *        method: 'post',
 *     });
 */
export const declare = <
    /**
     * The payload to send to the server
     */
    Params,
    Schema extends z.ZodType,
    Path extends string = string
>({
    path,
    schema,
    method
}: ApiEndpointDeclaration<Schema, Path>) => {
    const parser = makeApiParser<Schema>(schema);
    type SchemaValidatedReturn = z.infer<Schema>;

    return async (
        params: MergePathParams<Params, Path>
    ): Promise<Either<ApiClientError, SchemaValidatedReturn>> => {
        const [interpolated, data] = interpolate(path, params);
        try {
            const response = await client(interpolated, { data, method });

            const parsed = parser(response.data);

            if (Either.isRight(parsed)) {
                return parsed;
            }

            const error = Either.unwrap(parsed);

            if (isMalformedResponse(error)) {
                traceMalformedResponse(interpolated, error);
                return Either.left(new MalformedResponseError(path, error));
            }

            return Either.left(error);
        } catch (error) {
            return Either.left(toNetworkError(interpolated, error));
        }
    };
};

type FetchDeclarationParams<S extends z.ZodType, P extends string = string> = Omit<
    ApiEndpointDeclaration<S, P>,
    'method'
>;

type MergePathParams<QueryParams, Path extends string> = QueryParams extends undefined
    ? keyof ExtractParams<Path> extends never
        ? never
        : ExtractParams<Path>
    : QueryParams & ExtractParams<Path>;

type MergePathParamsAsArgs<QueryParams, Path extends string> =
    MergePathParams<QueryParams, Path> extends never ? [] : [MergePathParams<QueryParams, Path>];

type FetchError = MalformedResponseError | NetworkError;
export type FetchResponse<T> = Either<FetchError, T>;

/**
 * Declarative client for read-only server calls, used in the same way as
 * `declare` but the HTTP method is always `get` and there are no
 * validation errors attached to the result. (Throws on malformed API payload)
 *
 * This can be called without explicit type args
 *
 * ```
 *   const fetchAddresses = declareFetch({ path: 'v3/user/addresses', schema: z.array($address) });
 * ```
 *
 * Usage:
 * ```
 *   const addresses = await fetchAddresses();
 * ```
 *
 * If using with useSWR:
 *
 * ```
 *   const { data: addresses, isLoading } = useSWR(fetchAddresses.swrKey(), fetchAddresses.swr);
 * ```
 *
 * Can be called with interpolated path params:
 *
 * ```
 *   const $orderStatusPayload = z.array($RemoteOrderStatus);
 *   const orderStatusPath = 'v1/Checkout/order/:orderId/delivery-status';
 *
 *   export const fetchOrderStatus = declareFetch<
 *       typeof $orderStatusPayload,
 *       undefined,
 *       typeof orderStatusPath
 *   >({
 *       path: orderStatusPath,
 *       schema: $orderStatusPayload,
 *   });
 * ```
 *
 * Usage:
 *
 * ```
 *    fetchOrderStatus({ orderId });
 *
 *    useSWR(fetchOrderStatus.swrKey({ orderId }), fetchOrderStatus.swr)
 * ```
 */
export const declareFetch = <
    Schema extends z.ZodType,
    Params = undefined,
    Path extends string = string
>({
    schema,
    path
}: FetchDeclarationParams<Schema, Path>) => {
    type Args = MergePathParamsAsArgs<Params, Path>;
    type SchemaValidatedReturn = z.infer<Schema>;

    const splitArgs = memo((...args: Args) => {
        const [merged] = args;
        return interpolate(path, merged);
    });

    type SWRArgs = ReturnType<typeof splitArgs>;

    const impl = async ([interpolated, params]: SWRArgs) => {
        const response = await client.get(interpolated, { params });

        const parsed = schema.safeParse(response.data);

        if (!parsed.success) {
            const error: MalformedResponse = {
                systemErrors: parsed.error,
                recovery: response.data
            };

            traceMalformedResponse(interpolated, error);
            throw new MalformedResponseError(interpolated, error);
        }

        return parsed.data as SchemaValidatedReturn;
    };

    // SWR can throw in network errors because useSWR catches/handles exceptions
    const swr = ([interpolated, params]: SWRArgs): Promise<SchemaValidatedReturn> => {
        return impl([interpolated, params]);
    };

    const fetcher = async (...args: Args): Promise<Either<FetchError, SchemaValidatedReturn>> => {
        const [merged] = args;
        const [interpolated, params] = interpolate(path, merged);

        try {
            const response = await impl([interpolated, params]);

            // zod 4 types a generic schema's output as `unknown` (zod 3 used `any`).
            return Either.right(response as NonNullable<SchemaValidatedReturn>);
        } catch (error) {
            if (isMalformedResponseError(error)) {
                return Either.left(error);
            }
            console.error('[declarativeClient.fetch]:', error);
            return Either.left(toNetworkError(interpolated, error));
        }
    };

    fetcher.swr = swr;
    fetcher.swrKey = splitArgs;

    return fetcher;
};

export const interpolate = <Path extends string, Params>(
    path: Path,
    merged?: MergePathParams<Params, Path>
): [string, QueryParams] => {
    if (merged === undefined) {
        return [path, {}];
    }

    const params: QueryParams = {};
    let interpolated: string = path;

    for (const key in merged) {
        const token = `:${key}`;
        const value = merged[key];

        if (interpolated.includes(token) && !!value) {
            interpolated = interpolated.replace(token, value as any);
            continue;
        }

        params[key] = value as any;
    }

    return [interpolated, params];
};

const memo = <T extends (...args: any[]) => any>(
    fn: T
): ((...funcArgs: Parameters<T>) => ReturnType<T>) => {
    const cache: Record<string, ReturnType<T>> = {};

    return (...args: Parameters<T>): ReturnType<T> => {
        const key = JSON.stringify(args);
        if (!cache[key]) {
            cache[key] = fn(...args);
        }
        return cache[key];
    };
};

const traceMalformedResponse = (path: string, malformedResponse: MalformedResponse) => {
    Sentry.addBreadcrumb({
        category: 'api',
        message: `ignoring API response validation failure from ${path}`,
        level: 'error',
        data: {
            payload: malformedResponse.recovery,
            error: malformedResponse.systemErrors
        }
    });

    Sentry.captureException(new MalformedResponseError(path, malformedResponse));
};

const toNetworkError = (path: string, error: unknown): NetworkError => {
    Sentry.addBreadcrumb({
        category: 'api',
        message: `Network error from the endpoint: ${path}`,
        level: 'error'
    });

    Sentry.captureException(error);

    if (axios.isAxiosError(error)) {
        const status = error.status;
        if (!status) {
            if (error.message === 'Network Error') {
                return {
                    reason: 'Unavailable',
                    message: error.message
                };
            }

            return {
                reason: 'Unknown',
                message: error.response?.data ?? 'An error has occurred, please try again'
            };
        }

        if (status >= 400 && status < 500) {
            return {
                reason: 'Unauthorized',
                message: error.response?.data ?? 'An unknown authorization error has occurred'
            };
        }

        return {
            reason: 'Unavailable',
            message: error.response?.data ?? 'A server error has occurred, please try again'
        };
    }

    if (error instanceof Error) {
        return {
            reason: 'Unknown',
            message: error.message
        };
    }

    return {
        reason: 'Unknown',
        message: 'An error has occurred, plese try again'
    };
};
